import { Router } from "vue-router";

import { mtpcProductKey, scccProductKey, tivProductKey, trwProductKey } from "@/types/permissions";
import { SimpleRoutingParams } from "@/types/routing";

import * as tmi from "@/constants";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
import { SpreadCalculatorKey, ToolsPath } from "@/constants";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export interface IRouteManager {
    // Utility functions
    handleForwardToSignInAndReturnAsync: ( returnPath: string, router: Router ) => Promise< void >;
    handleRedirectWithOptionalExplicitPath: ( router: Router, explicitNextRoute?: string ) => void;
    isHomePath: ( path: string ) => boolean;
    isMatchingPath: ( path: string, areaPath: string ) => boolean;
    isSignInPath: ( path: string ) => boolean;
    isSignUpPath: ( path: string ) => boolean;
    isSignInAsUserName: ( path: string | undefined ) => boolean;

    // My-Library link functions
    myArticlesPath: () => string;
    myWebinarsPath: () => string;

    // Stock/ETF details link functions
    buildSecurityPath: ( ticker: string ) => string;
    buildSecurityOptionsPath: ( ticker: string ) => string;

    authorRouteBySlug: ( slug: string ) => string;
    articleRouteBySlug: ( slug: string ) => string;
    courseRouteBySlug: ( slug: string ) => string;
    relatedArticlesRouteBySlug: ( slug: string ) => string;
    bonusReportsRouteByProductSlug: ( slug: string ) => string;
    webinarRouteBySlug: ( slug: string ) => string;
    sectorDetailsRouteBySymbol: ( slug: string ) => string;
    tagRouteBySlug: ( slug: string ) => string;
    tradeAlertRouteBySlug: ( slug: string ) => string;

    // Product link functions
    productRouteBySlug: ( productSlug: string ) => string;

    // Header/Footer links
    aboutUsPath: () => string;
    aboutVersionPath: () => string;
    articleSearchPath: () => string;
    bonusReportsSearchPath: () => string;
    contactUsPath: () => string;
    coursesPath: () => string;
    etfListingPath: () => string;
    forbiddenPath: () => string;
    homePath: () => string;
    homeName: () => string;
    majorIndexBreadthPath: () => string;
    sectorEdgePath: () => string;
    nyseBullishPercentIndexPath: () => string;
    optionsCalculatorPath: () => string;
    portfolioBuilderPath: () => string;
    positionSizeCalculatorPath: () => string;
    productsBasePath: () => string;
    profileBasePath: () => string;
    relativeStrengthMatrixPath: () => string;
    sectorAlertsPath: () => string;
    sectorBullishPercentIndicesPath: () => string;
    sectorListingPath: () => string;
    sectorTreePath: () => string;
    signInPath: () => string;
    signUpPath: () => string;
    spreadCalculatorPath: () => string;
    tradeAlertsPath: () => string;
    tradeOfTheDayPath: () => string;
    webinarSearchPath: () => string;
    sectorFavoritesPath: () => string;
    sectorFavoritesName: () => string;
    sectorsName: () => string;
    sectorsPath: () => string;
    privacyPolicyPath: () => string;
    refundPolicyPath: () => string;
    smsTextPolicyPath: () => string;
    termsAndConditionsPath: () => string;
    whitelistInstructionsPath: () => string;
    myLibraryPath: () => string;
    resetPasswordRoute: () => string;

    legalNavigation: () => Array< SimpleRoutingParams >;
    socialNavigation: () => Array< SimpleRoutingParams >;

    isCheckPointTrader: ( slug: string ) => boolean;
    isDarkMoneyIndicator: ( slug: string ) => boolean;
    isDeepMarketTrader: ( slug: string ) => boolean;
    isMonthlyTrendTrader: ( slug: string ) => boolean;
    isOptionsSoup: ( slug: string ) => boolean;
    isProfitSkimmer: ( slug: string ) => boolean;
    isSectorFocus: ( slug: string ) => boolean;
    isSectorProphets: ( slug: string ) => boolean;
    isTechnicalAnalysisMillionaire: ( slug: string ) => boolean;
    isTrueSovereignProfits: ( slug: string ) => boolean;
    isMegaTrendProfitClubInvestor: ( slug: string ) => boolean;
    isSmallCapCashCows: ( slug: string ) => boolean;
    appStatusPath: () => string;
    appToolsPath: () => string;
    featureNewsPath: () => string;
}

const spreadCalculatorPath: string = `${ ToolsPath }/${ SpreadCalculatorKey }` as const;

// TODO: there is no state, so these should just be utility functions...uggh
class RouteManager implements  IRouteManager {
    public async handleForwardToSignInAndReturnAsync( returnPath: string, router: Router ): Promise< void > {
        logger.debug( `Storing current path ('${ returnPath }') and forwarding to the sign-in page.` );
        sessionStorage.setItem( 'nextLinkAfterLogin', returnPath );
        await router.push( { path: routeManager.signInPath() } );
    }

    public handleRedirectWithOptionalExplicitPath( router: Router, explicitNextRoute?: string ): void {
        // Default to "Home" (Dashboard when logged in)
        let forwardingPath: string = this.homePath();
        logger.debug( `Defaulting to the standard landing page ('${ forwardingPath }').` );

        // If the call provided a "preferred" path, use it
        if ( explicitNextRoute ) {
            logger.debug( `Explicit forwarding path ('${ explicitNextRoute }') provided.` );
            forwardingPath = explicitNextRoute;
        }
        // No explicit path, use the path cached in localstorage, if it exists
        else {
            const nextLink: string | null = sessionStorage.getItem( 'nextLinkAfterLogin' );
            if ( nextLink ) {
                logger.debug( `No explicit forwarding path provided, forwarding to the path stored in localstorage ('${ nextLink }').` );
                forwardingPath = nextLink;
            }
        }

        logger.debug( `Clearing any 'next link' from cache and forwarding to ${ forwardingPath }.` );
        sessionStorage.removeItem( 'nextLinkAfterLogin' );
        router.push( { path: forwardingPath } ).then();
    }

    public myArticlesPath(): string { return tmi.MyArticlesPath; }
    public myWebinarsPath(): string { return tmi.MyWebinarsPath; }
    public bonusReportsSearchPath(): string { return tmi.BonusReportsSearchPath; }
    public buildSecurityPath( ticker: string ): string { return tmi.SecurityDetailsPath.replace( ':ticker', ticker.toLowerCase() ); }
    public buildSecurityOptionsPath( ticker: string ): string { return tmi.SecurityOptionsPath.replace( ':ticker', ticker.toLowerCase() ); }

    public isCheckPointTrader( slug: string ): boolean { return tmi.CheckPointTraderSlug === slug; }
    public isDarkMoneyIndicator( slug: string ): boolean { return tmi.DarkMoneyIndicatorSlug === slug; }
    public isDeepMarketTrader( slug: string ): boolean { return tmi.DeepMarketTraderSlug === slug; }
    public isMonthlyTrendTrader( slug: string ): boolean { return tmi.MonthlyTrendTraderSlug === slug; }
    public isOptionsSoup( slug: string ): boolean { return tmi.OptionsSoupSlug === slug; }
    public isProfitSkimmer( slug: string ): boolean { return tmi.ProfitSkimmerSlug === slug; }
    public isSectorFocus( slug: string ): boolean { return tmi.SectorFocusSlug === slug; }
    public isSectorProphets( slug: string ): boolean { return tmi.SectorProphetsSlug === slug; }
    public isTechnicalAnalysisMillionaire( slug: string ): boolean { return tmi.TechnicalAnalysisMillionaireSlug === slug; }
    public isTrueSovereignProfits( slug: string ): boolean { return tmi.TrueSovereignProfitsSlug === slug; }
    public isMegaTrendProfitClubInvestor( slug: string ): boolean { return tmi.MegaTrendProfitClubSlug === slug; }
    public isSmallCapCashCows( slug: string ): boolean { return tmi.SmallCapCashCowsSlug === slug; }

    public isHomePath( path: string ): boolean { return this.isMatchingPath( path, tmi.HomePath ); }
    public isSignInPath( path: string ): boolean { return this.isMatchingPath( path, tmi.SignInPath ); }
    public isSignUpPath( path: string ): boolean { return this.isMatchingPath( path, tmi.SignUpPath ); }
    public isSignInAsUserName( path: string | undefined | null ): boolean { return !!( path ) && this.isMatchingPath( path, tmi.SignInAsUserPath ); }
    public isMatchingPath( path: string, areaPath: string ): boolean { return path?.toLowerCase() === areaPath; }

    // Calculated routes (id-based)
    public authorRouteBySlug( slug: string ): string { return tmi.AuthorBioPath.replace( ':slug', slug ); }
    public articleRouteBySlug( slug: string ): string { return tmi.ArticlePath.replace( ':slug', slug ); }
    public courseRouteBySlug( slug: string ): string { return tmi.CoursePath.replace( tmi.CourseSlugKey, slug ); }
    public relatedArticlesRouteBySlug( slug: string ): string { return tmi.RelatedArticlesPath.replace( ':slug', slug ); }
    public bonusReportsRouteByProductSlug( slug: string ): string { return tmi.BonusReportsForProductPath.replace( ':productSlug', slug ); }
    public webinarRouteBySlug( slug: string ): string { return tmi.WebinarPath.replace( ':slug', slug ); }
    public sectorDetailsRouteBySymbol( sectorSymbol: string ): string { return tmi.SectorDetailsPath.replace( ':sector', sectorSymbol.toLowerCase() ) }

    public productRouteBySlug( productSlug: string ): string {
        // Re-mapping logic to handle the poorly planned rollout of the re-branding of TIV/TRW...
        let slug: string = productSlug;
        if ( slug.includes( tivProductKey ) ) {
            slug = slug.replace( tivProductKey, mtpcProductKey );
        } else if ( slug.includes( trwProductKey ) ) {
            slug = slug.replace( trwProductKey, scccProductKey );
        }

        return tmi.ProductsPath.replace( ':productSlug', slug );
    }

    public tagRouteBySlug( alertSlug: string ): string {
        return tmi.TagPath.replace( ':slug', alertSlug );
    }

    public tradeAlertRouteBySlug( alertSlug: string ): string {
        return tmi.TradeAlertPath.replace( ':slug', alertSlug );
    }

    // Header/Footer links
    public aboutUsPath(): string {
        return tmi.AboutUsPath;
    }

    public aboutVersionPath(): string {
        return tmi.AboutVersionPath;
    }

    public articleSearchPath(): string {
        return tmi.ArticleSearchPath;
    }

    public contactUsPath(): string {
        return tmi.ContactUsPath;
    }

    public coursesPath(): string {
        return tmi.CoursesPath;
    }

    public forbiddenPath(): string {
        return tmi.ForbiddenPath;
    }

    /**
     * Returns the configured relative path to the standard landing page ("Home" or "Dashboard")
     * @returns {string} The relative path to the configured Home/Dashboard page
     */
    public homePath(): string {
        return tmi.HomePath;
    }

    public homeName(): string {
        return tmi.HomeName;
    }

    public sectorEdgePath(): string {
        return tmi.SectorEdgePath;
    }

    public productsBasePath(): string {
        return tmi.ProductsPath;
    }

    public profileBasePath(): string {
        return tmi.ProfilePath;
    }

    public sectorAlertsPath(): string {
        return tmi.SectorAlertsPath;
    }

    public signInPath(): string {
        return tmi.SignInPath;
    }

    public signUpPath(): string {
        return tmi.SignUpPath;
    }

    public tradeAlertsPath(): string {
        return tmi.TradeAlertsPath;
    }

    public tradeOfTheDayPath(): string {
        return tmi.TradeOfTheDayPath;
    }

    public sectorBullishPercentIndicesPath(): string {
        return tmi.SectorBullishPercentIndicesPath;
    }

    public nyseBullishPercentIndexPath(): string {
        return tmi.NyseBpiPath;
    }

    public relativeStrengthMatrixPath(): string {
        return tmi.RelativeStrengthMatrixPath;
    }

    public majorIndexBreadthPath(): string {
        return tmi.MajorIndexBreadthPath;
    }

    public etfListingPath(): string {
        return tmi.EtfListingPath;
    }

    public sectorTreePath(): string {
        return tmi.SectorTreePath;
    }

    public sectorListingPath(): string {
        return tmi.SectorsPath;
    }

    public portfolioBuilderPath(): string {
        return tmi.PortfolioBuilderPath;
    }

    public optionsCalculatorPath(): string {
        return tmi.OptionsCalculatorPath;
    }

    public positionSizeCalculatorPath(): string {
        return tmi.PositionSizeCalculatorPath;
    }

    public spreadCalculatorPath(): string {
        return spreadCalculatorPath;
    }

    public webinarSearchPath(): string {
        return tmi.WebinarSearchPath;
    }

    public sectorFavoritesPath(): string {
        return tmi.SectorsFavoritesPath;
    }

    public sectorFavoritesName(): string {
        return tmi.SectorFavoritesName;
    }

    public sectorsName(): string {
        return tmi.Sectors;
    }

    public sectorsPath(): string {
        return tmi.SectorsPath;
    }

    public legalNavigation(): Array< SimpleRoutingParams > {
        return [ {
            key: 'lblPrivacyPolicy',
            path: this.privacyPolicyPath(),
            icon: 'pi-shield'
        }, {
            key: 'lblRefundPolicy',
            path: this.refundPolicyPath(),
            icon: 'pi-wallet'
        }, {
            key: 'lblSmsPolicy',
            path: this.smsTextPolicyPath(),
            icon: 'pi-send'
        }, {
            key: 'lblTermsAndConditions',
            path: this.termsAndConditionsPath(),
            icon: 'pi-briefcase'
        }, {
            key: 'lblWhitelistInstructions',
            path: this.whitelistInstructionsPath(),
            icon: 'pi-list-check'
        } ];
    }

    public socialNavigation(): Array< SimpleRoutingParams > {
        return [ {
            key: 'lblFacebook',
            path: "https://www.facebook.com/chrisroweshow",
            icon: 'pi-facebook',
        }, {
            key: 'lblX',
            path: "https://twitter.com/ChrisRoweShow",
            icon: 'pi-twitter',
        }, {
            key: 'lblInstagram',
            path: "https://www.instagram.com/chrisroweshow/",
            icon: 'pi-instagram',
        }, {
            key: 'lblYouTube',
            path: "https://www.youtube.com/@ChrisRoweShow",
            icon: 'pi-youtube',
        }, {
            key: 'lblDiscord',
            path: "https://discord.gg/",
            icon: 'pi-discord',
        } ];
    }

    public privacyPolicyPath(): string {
        return tmi.PrivacyPolicyPath;
    }

    public refundPolicyPath(): string {
        return tmi.RefundPolicyPath;
    }

    public smsTextPolicyPath(): string {
        return tmi.SmsTextPolicyPath;
    }

    public termsAndConditionsPath(): string {
        return tmi.TermsOfUsePolicyPath;
    }

    public whitelistInstructionsPath(): string {
        return tmi.WhitelistInstructionsPath;
    }

    public myLibraryPath(): string {
        return tmi.MyLibraryPath;
    }

    public resetPasswordRoute(): string {
        return tmi.ResetPasswordPath;
    }

    //Support Links
    public appStatusPath(): string {
        return tmi.AppStatusPath;
    }
    public appToolsPath(): string {
        return tmi.AppToolsPath;
    }
    public featureNewsPath(): string {
        return tmi.FeatureNewsPath;
    }
}

const routeManager: IRouteManager = new RouteManager();
export default routeManager;
